"use client"

import { useState } from "react"
import {
  Box, Button, Flex, Heading, Textarea, VStack,
  List, ListItem, Text, FormControl, IconButton, useToast,
  Tooltip, useColorModeValue
} from "@chakra-ui/react"
import { ArrowRightIcon, ArrowLeftIcon } from "@chakra-ui/icons"
import { BiTransfer } from "react-icons/bi";
import { AiOutlineClear } from "react-icons/ai";
import PageHeading from "../../components/PageHeading"

export default function Base64Converter({ view="decode" }) {
  const [isDecodeView, setIsDecodeView] = useState(view === "decode")
  const [encodedText, setEncodedText] = useState("")
  const [decodedText, setDecodedText] = useState("")
  const [history, setHistory] = useState([])
  const toast = useToast()
  const historyListItemBg = useColorModeValue("gray.100", "gray.700");

  const encode = () => {
    try {
      const encoded = btoa(decodedText) //decodedText.split("\n").map(text => btoa(text)).join('\n')
      setEncodedText(encoded)
      addToHistory(encoded, decodedText, "encode")
    } catch (error) {
      showError("Error encoding: Invalid input")
    }
  }

  const decode = () => {
    try {
      const decoded = encodedText.split("\n").map(text => atob(text)).join('\n')
      setDecodedText(decoded)
      addToHistory(encodedText, decoded, "decode")
    } catch (error) {
      showError("Error decoding: Invalid base64 input")
    }
  }

  const addToHistory = (encoded, decoded, type) => {
    setHistory((prev) => [{ encoded, decoded, type }, ...prev.slice(0, 9)])
  }

  const showError = (message) => {
    toast({
      title: "Error",
      description: message,
      status: "error",
      duration: 3000,
      isClosable: true,
    })
  }

  return (
    <>
      <PageHeading
        mainText={`Base64 ${isDecodeView ? "Decoder/Encoder" : "Encoder/Decoder"}`}
        subText={`A free online tool to ${isDecodeView ? "decode and encode" : "encode and decode"} text to and from Base64`}
      />
      <Flex justify="center" mb={8}>
        <Tooltip shouldWrapChildren hasArrow label="Switch Position">
          <IconButton
            aria-label="Switch Conversion"
            key={"ghost"}
            variant={"ghost"}
            rounded="full"
            onClick={(e) => setIsDecodeView(!isDecodeView)}
          >
            <BiTransfer />
          </IconButton>
        </Tooltip>
      </Flex>
      <Flex direction={{ base: "column", md: "row" }} gap={4}>
        <FormControl order={ isDecodeView ? "1" : "3"}>
          <Heading as="h2" size="sm" mb={2}>Encoded Text</Heading>
          <Textarea
            value={encodedText}
            onChange={(e) => setEncodedText(e.target.value)}
            placeholder={isDecodeView ? "Text to decode" : "Output"} 
            height="200px"
            minHeight={"40vh"}
          />
        </FormControl>
        <VStack justify="center" order="2">
          <Button rightIcon={<ArrowRightIcon />} onClick={isDecodeView ? decode : encode}>
            {isDecodeView ? "Decode" : "Encode"}
          </Button>
          <Button leftIcon={<ArrowLeftIcon />} onClick={isDecodeView ? encode : decode}>
            {isDecodeView ? "Encode" : "Decode"}
          </Button>
        </VStack>
        <FormControl order={ isDecodeView ? "3" : "1"}>
          <Heading as="h2" size="sm" mb={2}>Plain Text</Heading>
          <Textarea
            value={decodedText}
            onChange={(e) => setDecodedText(e.target.value)}
            placeholder={isDecodeView ? "Output" : "Text to encode"}
            height="200px"
            minHeight={"40vh"}
            style={{ wordBreak: "break-all", flex: 1 }}
          />
        </FormControl>
      </Flex>
      <Box mt={12}>
        <Heading as="h2" size="sm" mb={4}>
          Conversion History
          <Tooltip shouldWrapChildren hasArrow label="Clear History">
            <IconButton
              aria-label="Clear History"
              key={"ghost"}
              variant={"ghost"}
              rounded="full"
              onClick={(e) => setHistory([])}
            >
              <AiOutlineClear />
            </IconButton>
          </Tooltip>
        </Heading>
        <List spacing={3}>
          {history.map((item, index) => (
            <ListItem key={index} p={2} bg={historyListItemBg} borderRadius="md">
              <Flex direction={{ base: "column", md: "row" }} gap={4}>
                <Text style={{ wordBreak: "break-all", flex: 1, whiteSpace: "pre-line" }} order={ isDecodeView ? "1" : "3"}>
                  {item.encoded}
                </Text>
                <Text fontWeight="bold" order="2">
                  {
                    ((item.type === "encode" && isDecodeView) || (item.type === "decode" && !isDecodeView))
                      ? <ArrowLeftIcon />
                      : <ArrowRightIcon />
                  }
                </Text>
                <Text style={{ wordBreak: "break-all", flex: 1, whiteSpace: "pre-line" }} order={ isDecodeView ? "3" : "1"}>
                  {item.decoded}
                </Text>
              </Flex>
            </ListItem>
          ))}
        </List>
      </Box>
    </>
  )
}

